import React, { useEffect, useMemo, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import Popover from '@/components/Popover';
import { toDateString, toTimeString, fromDMA, parseDateTime } from '@/date';
import DatePicker from '@/components/DatePicker';
import DatePresets from '@/components/DatePresets';
import MaskedInput from '@/components/Inputs/MaskedInput';
import Icon from '@/components/Icon';
import Button from '@/components/Button';
import Form from '@/components/Form';
import FormField from '@/components/FormField';
import * as Yup from 'yup';

const propTypes = {
  dueAt: PropTypes.object,
  onSubmit: PropTypes.func.isRequired,
  children: PropTypes.node,
  isAllDay: PropTypes.bool
};

const defaultProps = {
  onSubmit: () => { }
};

const schema = Yup.object().shape({
  time: Yup.string()
    // eslint-disable-next-line no-magic-numbers
    .min(5, 'Digite um horário válido no formato HH:mm. Ex: 12:00.'),
  date: Yup.string().required('Selecione uma data.')
});

const PopoverContent = ({ onSubmit, onHide, dueAt, isAllDay }) => {
  const [dueAtCopy, setDueAtCopy] = useState(dueAt);

  const defaultValues = useMemo(() => ({
    date: toDateString(dueAt),
    time: isAllDay ? '' : toTimeString(dueAt)
  }), [dueAt]);

  const handleDateChange = (setFieldValue, dateString) => {
    setFieldValue('date', dateString);
    setDueAtCopy(fromDMA(dateString));
  };

  const handleSubmit = ({ date, time }) => {
    const dateTimeString = `${date} ${time ? time : '23:59:59'}`;
    const dateTime = parseDateTime(dateTimeString, !time);

    onSubmit(dateTime);
    onHide();
  };

  return (
    <Form
      name='date-time-picker'
      onSubmit={(data) => handleSubmit(data, onHide)}
      defaultValues={defaultValues}
      validationSchema={schema}
    >
      {
        ({ setFieldValue, handleBlur }) => (
          <>
            <FormField
              name='date'
              as={DatePicker}
              mode='uncontrolled'
              numberOfMonths={1}
              month={dueAtCopy}
              onDayClick={(date) => handleDateChange(setFieldValue, toDateString(date))}
              selected={dueAtCopy}
              fixedWeeks
            />

            <FormField
              name='date'
              as={DatePresets}
              handleChange={(dateString) => handleDateChange(setFieldValue, dateString)}
            />

            <FormField
              as={MaskedInput}
              name='time'
              maskType='time'
              hint='Opcional'
              className='mt-3'
              rightAdornment={() => (
                <Button
                  onClick={() => setFieldValue('time', '')}
                  size='sm'
                  variant='link'
                  className={classnames(
                    'position-absolute',
                    'top-50',
                    'end-0',
                    'translate-middle-y',
                    'rounded',
                    'p-2',
                    'border-0'
                  )}
                  aria-label='Limpar'
                >
                  <Icon name='close' />
                </Button>
              )}
              variant='white'
              label='Horário'
              placeholder='00:00'
              handleBlur={handleBlur}
            />

            <Button type='submit' className='mt-3 w-100'>
              Salvar
            </Button>
          </>
        )
      }
    </Form>
  );
};
function DateTimePicker({ dueAt, onSubmit, children, isAllDay }) {
  const [isHided, setIsHided] = useState(true);
  const ref = useRef(null);

  useEffect(() => {
    if (ref.current) {
      ref.current.setShow(!isHided);
    }
  }, [isHided]);

  const showPopover = () => {
    setIsHided(false);
  };

  return (
    <div>
      <Popover
        ref={ref}
        hide={isHided}
        className='max-width-7'
        flip
        content={({ onHide }) => (
          !isHided && (
            <PopoverContent
              key={isHided}
              onSubmit={onSubmit}
              dueAt={dueAt}
              onHide={onHide}
              isAllDay={isAllDay}
            />
          )
        )}
      >
        {children(showPopover)}
      </Popover>
    </div>
  );
}

DateTimePicker.propTypes = propTypes;
DateTimePicker.defaultProps = defaultProps;

export default DateTimePicker;
